export const PROJECT_MEMBERSHIP_EXPIRING_DAYS_DEFAULT = 7;
export const PROJECT_MEMBERSHIP_EXPIRING_DAYS_MIN = 0;
export const PROJECT_MEMBERSHIP_EXPIRING_DAYS_MAX = 90;

export function normalizeMembershipExpiringDays(
  value,
  fallback = PROJECT_MEMBERSHIP_EXPIRING_DAYS_DEFAULT,
) {
  const fallbackNumber = Number(fallback);
  const safeFallback = Number.isInteger(fallbackNumber)
    && fallbackNumber >= PROJECT_MEMBERSHIP_EXPIRING_DAYS_MIN
    && fallbackNumber <= PROJECT_MEMBERSHIP_EXPIRING_DAYS_MAX
    ? fallbackNumber
    : PROJECT_MEMBERSHIP_EXPIRING_DAYS_DEFAULT;
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return safeFallback;
  if (parsed < PROJECT_MEMBERSHIP_EXPIRING_DAYS_MIN || parsed > PROJECT_MEMBERSHIP_EXPIRING_DAYS_MAX) {
    return safeFallback;
  }
  return parsed;
}

export function normalizeProjectMembershipSettings(settings = {}, current = {}) {
  const source = settings && typeof settings === "object" ? settings : {};
  const previous = current && typeof current === "object" ? current : {};
  const previousExpiringDays = normalizeMembershipExpiringDays(previous.expiringDays);
  return {
    expiringDays: normalizeMembershipExpiringDays(source.expiringDays, previousExpiringDays),
  };
}

export const PROJECT_CAPABILITIES = Object.freeze([
  { key: "memberships", label: "Membresías" },
  { key: "gastronomicMenu", label: "Carta gastronómica" },
  { key: "tables", label: "Mesas" },
  { key: "tableReservations", label: "Reservas de mesa" },
  { key: "kitchenOrders", label: "Comandas" },
  { key: "recipes", label: "Recetas" },
  { key: "properties", label: "Propiedades" },
  { key: "leases", label: "Contratos y alquileres" },
  { key: "leaseCollections", label: "Cobranzas de alquileres" },
]);

const CAPABILITY_KEYS = PROJECT_CAPABILITIES.map(({ key }) => key);

const CAPABILITY_MODULE_KEYS = Object.freeze({
  memberships: "memberships",
  gastronomicMenu: "restaurant_menu",
  tables: "tables",
  tableReservations: "table_reservations",
  kitchenOrders: "kitchen_orders",
  recipes: "recipes",
  properties: "properties",
  leases: "leases",
  leaseCollections: "lease_collections",
});

export function normalizeProjectCapabilities(capabilities = {}) {
  const source = capabilities && typeof capabilities === "object" ? capabilities : {};
  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, source?.[key] === true]),
  );
}

export function isProjectCapabilityEnabled(project, capabilityKey) {
  if (!CAPABILITY_KEYS.includes(String(capabilityKey || ""))) return false;
  const capabilityEnabled = normalizeProjectCapabilities(project?.capabilities)[capabilityKey] === true;
  const legacyModuleKey = CAPABILITY_MODULE_KEYS[capabilityKey];
  return capabilityEnabled || project?.modules?.[legacyModuleKey] === true;
}

export function effectiveProjectCapabilities(project = {}) {
  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, isProjectCapabilityEnabled(project, key)]),
  );
}

export function activeProjectCapabilities(project) {
  return PROJECT_CAPABILITIES.filter(({ key }) => isProjectCapabilityEnabled(project, key));
}

export const PROJECT_BUSINESS_PROFILES = Object.freeze([
  {
    key: "commerce",
    label: "Comercio",
    description: "Catálogo, pedidos y cobros sin sumar funciones específicas por rubro.",
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "services",
    label: "Servicios",
    description: "Landing, agenda, CRM y cobros sin imponer una estructura rígida.",
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "gym",
    label: "Gimnasio",
    description: "Sugiere membresías y mantiene disponibles el resto de las capacidades opcionales.",
    suggestedCapabilities: Object.freeze(["memberships"]),
  },
  {
    key: "restaurant",
    label: "Restaurante",
    description: "Sugiere carta, mesas, reservas, comandas y recetas sobre la operación existente.",
    suggestedCapabilities: Object.freeze([
      "gastronomicMenu",
      "tables",
      "tableReservations",
      "kitchenOrders",
      "recipes",
    ]),
  },
  {
    key: "real_estate",
    label: "Inmobiliaria",
    description: "Sugiere propiedades, contratos/alquileres y cobranzas como capacidades independientes.",
    suggestedCapabilities: Object.freeze([
      "properties",
      "leases",
      "leaseCollections",
    ]),
  },
]);


export const PROJECT_BUSINESS_GOALS = Object.freeze([
  {
    key: "sellProducts",
    label: "Vender productos",
    profiles: Object.freeze(["commerce"]),
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "offerServices",
    label: "Ofrecer servicios",
    profiles: Object.freeze(["services", "gym"]),
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "manageCustomers",
    label: "Gestionar clientes",
    profiles: Object.freeze(["commerce", "services", "gym", "restaurant"]),
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "manageMemberships",
    label: "Gestionar membresías",
    profiles: Object.freeze(["gym"]),
    suggestedCapabilities: Object.freeze(["memberships"]),
  },
  {
    key: "takeReservations",
    label: "Tomar reservas",
    profiles: Object.freeze(["services", "gym", "restaurant"]),
    suggestedCapabilities: Object.freeze([]),
    suggestedCapabilitiesByProfile: Object.freeze({
      restaurant: Object.freeze(["tables", "tableReservations"]),
    }),
  },
  {
    key: "gastronomicMenu",
    label: "Usar carta gastronómica",
    profiles: Object.freeze(["restaurant"]),
    suggestedCapabilities: Object.freeze(["gastronomicMenu"]),
  },
  {
    key: "controlBusiness",
    label: "Controlar el negocio",
    profiles: Object.freeze(["commerce", "services", "gym", "restaurant", "real_estate"]),
    suggestedCapabilities: Object.freeze([]),
  },
  {
    key: "manageProperties",
    label: "Gestionar propiedades",
    profiles: Object.freeze(["real_estate"]),
    suggestedCapabilities: Object.freeze(["properties"]),
  },
  {
    key: "manageLeases",
    label: "Gestionar contratos y alquileres",
    profiles: Object.freeze(["real_estate"]),
    suggestedCapabilities: Object.freeze(["leases"]),
  },
  {
    key: "manageLeaseCollections",
    label: "Gestionar cobranzas de alquileres",
    profiles: Object.freeze(["real_estate"]),
    suggestedCapabilities: Object.freeze(["leaseCollections"]),
  },
]);

export function businessGoalsForProfile(value) {
  const profile = normalizeProjectBusinessProfile(value);
  if (!profile) return [];
  return PROJECT_BUSINESS_GOALS.filter((goal) => goal.profiles.includes(profile));
}

export function normalizeProjectBusinessGoals(values = []) {
  const allowed = new Set(PROJECT_BUSINESS_GOALS.map((goal) => goal.key));
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter((value) => allowed.has(value)),
    ),
  );
}

export function projectBusinessGoals(project) {
  const stored = Array.isArray(project?.onboarding?.businessGoals)
    ? project.onboarding.businessGoals
    : [];
  return normalizeProjectBusinessGoals(stored);
}

export function hasGuidedProjectStart(project) {
  if (project?.onboarding?.guidedStart !== true) return false;
  return Boolean(
    normalizeProjectBusinessProfile(project?.businessProfile) ||
      projectBusinessGoals(project).length,
  );
}

const STARTER_AREAS_BY_GOAL = Object.freeze({
  sellProducts: Object.freeze(["items", "orders"]),
  offerServices: Object.freeze(["items", "agenda"]),
  manageCustomers: Object.freeze(["customers"]),
  manageMemberships: Object.freeze(["customers", "system"]),
  takeReservations: Object.freeze(["agenda", "customers"]),
  gastronomicMenu: Object.freeze(["items", "orders"]),
  controlBusiness: Object.freeze(["system", "stats"]),
  manageProperties: Object.freeze(["landing", "system"]),
  manageLeases: Object.freeze(["customers", "system"]),
  manageLeaseCollections: Object.freeze(["customers", "system"]),
});

const STARTER_AREAS_BY_PROFILE = Object.freeze({
  commerce: Object.freeze(["items", "orders"]),
  services: Object.freeze(["items", "agenda", "customers"]),
  gym: Object.freeze(["customers", "agenda", "items"]),
  restaurant: Object.freeze(["items", "orders", "system"]),
  real_estate: Object.freeze(["landing", "customers", "system"]),
});

export function projectStarterAreas(project) {
  if (!hasGuidedProjectStart(project)) return [];

  const profile = normalizeProjectBusinessProfile(project?.businessProfile);
  const goals = projectBusinessGoals(project);
  const areas = ["landing"];

  for (const goal of goals) {
    areas.push(...(STARTER_AREAS_BY_GOAL[goal] || []));
  }

  if (!goals.length) {
    areas.push(...(STARTER_AREAS_BY_PROFILE[profile] || []));
  }

  if (isProjectCapabilityEnabled(project, "memberships")) {
    areas.push("customers", "system");
  }
  if (isProjectCapabilityEnabled(project, "gastronomicMenu")) {
    areas.push("items", "orders");
  }
  if (
    isProjectCapabilityEnabled(project, "tables") ||
    isProjectCapabilityEnabled(project, "tableReservations")
  ) {
    areas.push("agenda", "system");
  }
  if (
    isProjectCapabilityEnabled(project, "kitchenOrders") ||
    isProjectCapabilityEnabled(project, "recipes")
  ) {
    areas.push("system", "items");
  }
  if (isProjectCapabilityEnabled(project, "properties")) {
    areas.push("landing", "system");
  }
  if (
    isProjectCapabilityEnabled(project, "leases") ||
    isProjectCapabilityEnabled(project, "leaseCollections")
  ) {
    areas.push("customers", "system");
  }

  return Array.from(new Set(areas));
}

export function capabilitiesForBusinessGoals(profileValue, goalKeys = []) {
  const profile = normalizeProjectBusinessProfile(profileValue);
  const selected = new Set(Array.isArray(goalKeys) ? goalKeys : []);
  const capabilities = normalizeProjectCapabilities({});

  for (const goal of PROJECT_BUSINESS_GOALS) {
    if (!selected.has(goal.key)) continue;
    const suggested = goal.suggestedCapabilitiesByProfile?.[profile]
      || goal.suggestedCapabilities
      || [];
    for (const capabilityKey of suggested) {
      if (CAPABILITY_KEYS.includes(capabilityKey)) capabilities[capabilityKey] = true;
    }
  }

  return capabilities;
}

export function normalizeProjectBusinessProfile(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return PROJECT_BUSINESS_PROFILES.some(({ key }) => key === normalized) ? normalized : "";
}

export function projectBusinessProfileDefinition(value) {
  const normalized = normalizeProjectBusinessProfile(value);
  return PROJECT_BUSINESS_PROFILES.find(({ key }) => key === normalized) || null;
}

export function capabilitiesForBusinessProfile(value) {
  const profile = projectBusinessProfileDefinition(value);
  const suggested = new Set(profile?.suggestedCapabilities || []);
  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, suggested.has(key)]),
  );
}

export function applyBusinessProfileSuggestions(capabilities, profileValue) {
  const current = normalizeProjectCapabilities(capabilities);
  const suggested = capabilitiesForBusinessProfile(profileValue);

  return Object.fromEntries(
    CAPABILITY_KEYS.map((key) => [key, current[key] === true || suggested[key] === true]),
  );
}

